// eslint-disable-next-line import/no-named-as-default
import Icon from "@mdi/react";

import {
  mdiAlertCircle,
  mdiAlertRhombus,
  mdiCheckCircle,
  mdiInformationOutline,
  mdiLoading
} from "@mdi/js";
import { Tooltip } from "antd";
import styled from "styled-components";

import { GeoModelJobStatus, InvalidItem } from "./models/GeoModelJob";
import {
  NO_WELLS_IN_GEO_MODEL_TOOLTIP,
  getJobErrorTooltip,
  isInProgressStatus,
  isNoWellsInGeoModel,
  isProgressStatus,
  truncateMessage
} from "./utils";

interface StatusPillProps {
  status?: Maybe<string>;
  errorMessage?: Maybe<string>;
  invalidItems?: Maybe<InvalidItem[]>;
  // True while the live status is still being fetched. Shown as "Checking…" so a
  // job whose status hasn't loaded yet never flashes as "Not started".
  loading?: Maybe<boolean>;
}

type StatusVariant =
  | "succeeded"
  | "failed"
  | "partial"
  | "running"
  | "notStarted"
  | "empty"
  | "unknown";

// Visual mapping per status. Running is blue; succeeded green; failed red;
// partial amber; not-started/unknown neutral grey.
const STATUS_STYLES: Record<
  StatusVariant,
  { label: string; color: string; background: string }
> = {
  succeeded: { label: "Succeeded", color: "#237804", background: "#f6ffed" },
  failed: { label: "Failed", color: "#cf1322", background: "#fff1f0" },
  partial: { label: "Partial", color: "#b45309", background: "#fff4e5" },
  running: {
    label: "Running",
    color: "var(--color-primary)",
    background: "var(--color-primary-10)"
  },
  notStarted: { label: "Not started", color: "#595959", background: "#f5f5f5" },
  empty: { label: "No results", color: "#595959", background: "#f5f5f5" },
  // label is unused for "unknown" — the raw status text is always shown instead
  // (see the render logic below) since this represents a status we don't recognize.
  unknown: { label: "", color: "#595959", background: "#f5f5f5" }
};

// Precise hover reason for a partially-completed run: the affected count plus the
// sanitized backend reason. The full affected list is delivered by email, not
// shown inline. The reason is sanitized upstream — never a raw exception text.
function buildPartialTooltip(
  errorMessage?: Maybe<string>,
  invalidItems?: Maybe<InvalidItem[]>
): string {
  const affected = invalidItems?.length ?? 0;
  const base =
    affected > 0
      ? `Partially completed. ${affected} ${
          affected === 1 ? "entity was" : "entities were"
        } left out of the results.`
      : "Partially completed. Some entities were left out of the results.";
  return errorMessage ? `${base} ${truncateMessage(errorMessage, 300)}` : base;
}

/**
 * Status is always shown as a non-interactive pill. Failed, empty and partial pills
 * reveal the precise, sanitized reason on hover (getJobErrorTooltip for a failed or
 * empty run, an affected-count + reason summary for a partial one) — never a raw
 * exception/class name (OWASP A10). Re-running a job is done from the Calculate
 * action, not the pill.
 */
export function StatusPill({
  status,
  errorMessage,
  invalidItems,
  loading
}: StatusPillProps) {
  if (loading) {
    return (
      <Pill data-testid="job-status-pill" $color="#595959" $background="#f5f5f5">
        <Icon path={mdiLoading} size={1} spin />
        Checking…
      </Pill>
    );
  }

  let variant: StatusVariant;
  let icon: string | null = null;
  let spin = false;
  let label: string | null = null;

  if (status === GeoModelJobStatus.Completed) {
    variant = "succeeded";
    icon = mdiCheckCircle;
  } else if (isNoWellsInGeoModel(status, invalidItems)) {
    // EVA-255: a red "Failed" pill would send the user to support over a selection the geo
    // model simply has nothing for.
    variant = "empty";
    icon = mdiInformationOutline;
  } else if (status === GeoModelJobStatus.Error) {
    variant = "failed";
    icon = mdiAlertCircle;
  } else if (status === GeoModelJobStatus.PartiallyCompleted) {
    variant = "partial";
    icon = mdiAlertRhombus;
  } else if (status === GeoModelJobStatus.Cancelling) {
    variant = "running";
    icon = mdiLoading;
    spin = true;
    label = "Cancelling";
  } else if (status === GeoModelJobStatus.Cancelled) {
    // Terminal cancelled state — neutral, no spinner. Distinct from "Not started"
    // so a cancelled run doesn't look like one that never ran.
    variant = "notStarted";
    label = "Cancelled";
  } else if (status === GeoModelJobStatus.Queued || status === "Started") {
    // Pre-progress phase: enqueued ("Queued"), or picked up by the worker but not
    // yet producing per-well progress ("Started"). Show "Queued" for both so the
    // pill only reads as active work once real "n/m" progress arrives.
    variant = "running";
    icon = mdiLoading;
    spin = true;
    label = "Queued";
  } else if (!status || status === GeoModelJobStatus.NotStarted) {
    variant = "notStarted";
  } else if (isProgressStatus(status)) {
    // Live progress delivered as the status itself, e.g. "40/128" (wells done /
    // total). Show it verbatim with a spinner so it stays compact.
    variant = "running";
    icon = mdiLoading;
    spin = true;
    label = status;
  } else if (isInProgressStatus(status)) {
    // Other in-progress backend strings ("Running"/"Calculating") that aren't
    // part of GeoModelJobStatus but represent a job currently in progress.
    variant = "running";
    icon = mdiLoading;
    spin = true;
  } else {
    // A truthy status that's neither settled nor on the in-progress allowlist —
    // an unrecognized future status. Render it neutrally (raw text, no spinner);
    // useJobStatus excludes it from polling for the same reason.
    variant = "unknown";
    label = status;
  }

  const { label: defaultLabel, color, background } = STATUS_STYLES[variant];

  const pill = (
    <Pill data-testid="job-status-pill" $color={color} $background={background}>
      {icon && <Icon path={icon} size={1} spin={spin} />}
      {label ?? defaultLabel}
    </Pill>
  );

  // Failed, empty and partial pills surface the precise reason on hover.
  if (variant === "failed") {
    return <Tooltip title={getJobErrorTooltip(errorMessage)}>{pill}</Tooltip>;
  }
  if (variant === "empty") {
    // The backend's own reason names the right entity ("wells" vs "sticks") and carries the Zone
    // advice, so the two cannot drift; the constant only covers a job that stored none.
    return (
      <Tooltip title={getJobErrorTooltip(errorMessage, NO_WELLS_IN_GEO_MODEL_TOOLTIP)}>
        {pill}
      </Tooltip>
    );
  }
  if (variant === "partial") {
    return (
      <Tooltip title={buildPartialTooltip(errorMessage, invalidItems)}>{pill}</Tooltip>
    );
  }

  return pill;
}

const Pill = styled.span<{ $color: string; $background: string }>`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 12px;
  border-radius: 100px;
  font-size: 13px;
  font-weight: 500;
  white-space: nowrap;
  color: ${(props) => props.$color};
  background-color: ${(props) => props.$background};
`;
